import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, Plus, Trash2, Edit, ExternalLink, Github, Calendar, MapPin, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const AdmProjects = () => {
  const { portfolioData, addProject, updateProject, deleteProject } = usePortfolio();
  const [projects, setProjects] = useState(portfolioData?.projects || []);
  const [isSaving, setIsSaving] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [modifiedProjects, setModifiedProjects] = useState(new Set());

  // Update local state when portfolioData changes
  useEffect(() => {
    setProjects(portfolioData?.projects || []);
    setModifiedProjects(new Set());
  }, [portfolioData]);

  const initialProjectState = {
    name: '',
    description: '',
    image: '',
    technologies: [],
    features: [],
    githubUrl: '',
    liveUrl: '',
    category: 'fullstack',
    date: '',
    location: ''
  };

  const [newProject, setNewProject] = useState(initialProjectState);

  const handleSave = async () => {
    if (modifiedProjects.size === 0) {
      toast.info('Aucune modification à enregistrer');
      return;
    }

    setIsSaving(true);
    try {
      const savePromises = projects
        .filter(project => modifiedProjects.has(project.id))
        .map(project => {
          const projectToSubmit = {
            ...project,
            image: project.image?.trim() || null,
            githubUrl: project.githubUrl?.trim() || null,
            liveUrl: project.liveUrl?.trim() || null
          };
          return updateProject(project.id, projectToSubmit);
        });
      
      const results = await Promise.all(savePromises);
      const allSuccessful = results.every(result => result?.success);
      if (allSuccessful) {
        setModifiedProjects(new Set());
        toast.success('Projets sauvegardés avec succès');
      } else {
        const failedResult = results.find(result => !result?.success);
        toast.error(failedResult?.message || 'Erreur lors de la sauvegarde des projets');
      }
    } catch (error) {
      // console.error('Error saving projects:', error);
      toast.error('Erreur lors de la sauvegarde des projets');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setProjects(portfolioData?.projects || []);
    setEditingProject(null);
    setShowAddForm(false);
    setNewProject(initialProjectState);
    setModifiedProjects(new Set());
    toast.success('Liste réinitialisée');
  };

  const handleProjectChange = (projectId, field, value) => {
    setProjects(prev => prev.map(project =>
      project.id === projectId ? { ...project, [field]: value } : project
    ));
    setModifiedProjects(prev => new Set([...prev, projectId]));
  };

  const handleTechnologiesChange = (projectId, value) => {
    const technologies = value.split(',').map(tech => tech.trim()).filter(tech => tech);
    handleProjectChange(projectId, 'technologies', technologies);
  };

  const handleFeaturesChange = (projectId, value) => {
    const features = value.split('\n').map(feature => feature.trim()).filter(feature => feature);
    handleProjectChange(projectId, 'features', features);
  };

  const handleProjectBlur = async (projectId) => {
    if (modifiedProjects.has(projectId) && editingProject === projectId) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        try {
          const projectToSubmit = {
            ...project,
            image: project.image?.trim() || null,
            githubUrl: project.githubUrl?.trim() || null,
            liveUrl: project.liveUrl?.trim() || null
          };
          
          const result = await updateProject(projectId, projectToSubmit);
          if (result?.success) {
            setModifiedProjects(prev => {
              const newSet = new Set(prev);
              newSet.delete(projectId);
              return newSet;
            });
            toast.success('Projet mis à jour avec succès');
          } else {
            toast.error(result?.message || 'Erreur lors de la mise à jour du projet');
          }
        } catch (error) {
          // console.error('Error updating project:', error);
          toast.error('Erreur lors de la mise à jour du projet');
        }
      }
    }
  };

  const handleAddProject = async () => {
    if (newProject.name && newProject.description) {
      try {
        // Validate URLs before sending to backend - send null for empty URLs
        const projectToSubmit = {
          ...newProject,
          image: newProject.image?.trim() || null,
          githubUrl: newProject.githubUrl?.trim() || null,
          liveUrl: newProject.liveUrl?.trim() || null
        };
        
        const result = await addProject(projectToSubmit);
        if (result?.success) {
          setNewProject(initialProjectState);
          setShowAddForm(false);
          setProjects(portfolioData?.projects || []);
          toast.success('Projet ajouté avec succès');
        } else {
          toast.error(result?.message || 'Erreur lors de la création du projet');
        }
      } catch (error) {
        // console.error('Error adding project:', error);
        toast.error('Erreur lors de la création du projet');
      }
    } else {
      toast.error('Veuillez remplir au moins le nom et la description du projet');
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const result = await deleteProject(projectId);
      if (result?.success) {
        setProjects(prev => prev.filter(project => project.id !== projectId));
        toast.success('Projet supprimé avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de la suppression du projet');
      }
    } catch (error) {
      // console.error('Error deleting project:', error);
      toast.error('Erreur lors de la suppression du projet');
    }
  };

  const startEditProject = (project) => {
    setEditingProject(project.id);
  };

  const finishEditProject = () => {
    setEditingProject(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Briefcase className="w-6 h-6 mr-2" />
          Gestion des Projets
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-gray-600 dark:text-gray-400">
          {projects.length} projet(s) dans le portfolio
        </p>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un projet
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nouveau Projet</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new-name" className="flex items-center mb-1">Nom du projet</Label>
                <Input
                  id="new-name"
                  value={newProject.name}
                  onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nom du projet"
                />
              </div>
              <div>
                <Label htmlFor="new-category" className="flex items-center mb-1">Catégorie</Label>
                <select
                  id="new-category"
                  value={newProject.category}
                  onChange={(e) => setNewProject(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-2 pb-1 pt-2 border rounded-md bg-white dark:bg-[#0a0a0a]"
                >
                  <option value="fullstack">Full Stack</option>
                  <option value="frontend">Frontend</option>
                  <option value="backend">Backend</option>
                  <option value="data">Data</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="new-description" className="flex items-center mb-1">Description</Label>
              <Textarea
                id="new-description"
                value={newProject.description}
                onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description du projet"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="new-date" className="flex items-center mb-1">Date</Label>
                <Input
                  id="new-date"
                  value={newProject.date}
                  onChange={(e) => setNewProject(prev => ({ ...prev, date: e.target.value }))}
                  placeholder="Janvier 2024"
                />
              </div>
              <div>
                <Label htmlFor="new-location" className="flex items-center mb-1">Localisation</Label>
                <Input
                  id="new-location"
                  value={newProject.location}
                  onChange={(e) => setNewProject(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Mali"
                />
              </div>
              <div>
                <Label htmlFor="new-image" className="flex items-center mb-1">URL de l'image (optionnel)</Label>
                <Input
                  id="new-image"
                  value={newProject.image}
                  onChange={(e) => setNewProject(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">Laissez vide si pas d'image</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new-github" className="flex items-center mb-1">GitHub URL (optionnel)</Label>
                <Input
                  id="new-github"
                  value={newProject.githubUrl}
                  onChange={(e) => setNewProject(prev => ({ ...prev, githubUrl: e.target.value }))}
                  placeholder="https://github.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">Laissez vide si pas de lien GitHub</p>
              </div>
              <div>
                <Label htmlFor="new-live" className="flex items-center mb-1">Live URL (optionnel)</Label>
                <Input
                  id="new-live"
                  value={newProject.liveUrl}
                  onChange={(e) => setNewProject(prev => ({ ...prev, liveUrl: e.target.value }))}
                  placeholder="https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Laissez vide si pas de site en ligne</p>
              </div>
            </div>
            <div>
              <Label htmlFor="new-technologies" className="flex items-center mb-1">Technologies (séparées par des virgules)</Label>
              <Textarea
                id="new-technologies"
                value={newProject.technologies.join(', ')}
                onChange={(e) => setNewProject(prev => ({ 
                  ...prev, 
                  technologies: e.target.value.split(',').map(tech => tech.trim()).filter(tech => tech)
                }))}
                placeholder="ReactJS, Node.js, MongoDB"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="new-features" className="flex items-center mb-1">Fonctionnalités (une par ligne)</Label>
              <Textarea
                id="new-features"
                value={newProject.features.join('\n')}
                onChange={(e) => setNewProject(prev => ({ 
                  ...prev, 
                  features: e.target.value.split('\n').map(feature => feature.trim()).filter(feature => feature)
                }))}
                placeholder="Fonctionnalité 1&#10;Fonctionnalité 2&#10;Fonctionnalité 3"
                rows={4}
              />
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddProject}>Ajouter le projet</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {projects.map((project) => (
          <Card key={project.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <Edit className="w-5 h-5 mr-2" />
                  {project.name}
                </CardTitle>
                <div className="flex space-x-2">
                  {editingProject === project.id ? (
                    <Button variant="outline" size="sm" onClick={finishEditProject}>
                      Terminer
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => startEditProject(project)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDeleteProject(project.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`name-${project.id}`} className="flex items-center mb-1">Nom du projet</Label>
                  <Input
                    id={`name-${project.id}`}
                    value={project.name}
                    onChange={(e) => handleProjectChange(project.id, 'name', e.target.value)}
                    onBlur={() => handleProjectBlur(project.id)}
                    disabled={editingProject !== project.id}
                  />
                </div>
                <div>
                  <Label htmlFor={`category-${project.id}`} className="flex items-center mb-1">Catégorie</Label>
                  <select
                    id={`category-${project.id}`}
                    value={project.category}
                    onChange={async (e) => {
                      handleProjectChange(project.id, 'category', e.target.value);
                      const currentProject = projects.find(p => p.id === project.id);
                      if (currentProject && editingProject === project.id) {
                        try {
                          const projectToSubmit = {
                            ...currentProject,
                            category: e.target.value,
                            image: currentProject.image?.trim() || null,
                            githubUrl: currentProject.githubUrl?.trim() || null,
                            liveUrl: currentProject.liveUrl?.trim() || null
                          };
                          const result = await updateProject(project.id, projectToSubmit);
                          if (result?.success) {
                            setModifiedProjects(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(project.id);
                              return newSet;
                            });
                            toast.success('Catégorie mise à jour avec succès');
                          } else {
                            toast.error(result?.message || 'Erreur lors de la mise à jour de la catégorie');
                          }
                        } catch (error) {
                          // console.error('Error updating category:', error);
                          toast.error('Erreur lors de la mise à jour de la catégorie');
                        }
                      }
                    }}
                    disabled={editingProject !== project.id}
                    className="w-full px-2 pb-1 pt-2 border rounded-md bg-white dark:bg-[#0a0a0a]"
                  >
                    <option value="fullstack">Full Stack</option>
                    <option value="frontend">Frontend</option>
                    <option value="backend">Backend</option>
                    <option value="data">Data</option>
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor={`description-${project.id}`} className="flex items-center mb-1">Description</Label>
                <Textarea
                  id={`description-${project.id}`}
                  value={project.description}
                  onChange={(e) => handleProjectChange(project.id, 'description', e.target.value)}
                  onBlur={() => handleProjectBlur(project.id)}
                  disabled={editingProject !== project.id}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor={`date-${project.id}`} className="flex items-center mb-1">
                    <Calendar className="w-4 h-4 mr-1" />
                    Date
                  </Label>
                  <Input
                    id={`date-${project.id}`}
                    value={project.date}
                    onChange={(e) => handleProjectChange(project.id, 'date', e.target.value)}
                    onBlur={() => handleProjectBlur(project.id)}
                    disabled={editingProject !== project.id}
                  />
                </div>
                <div>
                  <Label htmlFor={`location-${project.id}`} className="flex items-center mb-1">
                    <MapPin className="w-4 h-4 mr-1" />
                    Localisation
                  </Label>
                  <Input
                    id={`location-${project.id}`}
                    value={project.location}
                    onChange={(e) => handleProjectChange(project.id, 'location', e.target.value)}
                    onBlur={() => handleProjectBlur(project.id)}
                    disabled={editingProject !== project.id}
                  />
                </div>
                <div>
                  <Label htmlFor={`image-${project.id}`} className="flex items-center mb-1">URL de l'image (optionnel)</Label>
                  <Input
                    id={`image-${project.id}`}
                    value={project.image}
                    onChange={(e) => handleProjectChange(project.id, 'image', e.target.value)}
                    onBlur={() => handleProjectBlur(project.id)}
                    disabled={editingProject !== project.id}
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Laissez vide si pas d'image</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`github-${project.id}`} className="flex items-center mb-1">
                    <Github className="w-4 h-4 mr-1" />
                    GitHub URL (optionnel)
                  </Label>
                  <Input
                    id={`github-${project.id}`}
                    value={project.githubUrl}
                    onChange={(e) => handleProjectChange(project.id, 'githubUrl', e.target.value)}
                    onBlur={() => handleProjectBlur(project.id)}
                    disabled={editingProject !== project.id}
                    placeholder="https://github.com/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Laissez vide si pas de lien GitHub</p>
                </div>
                <div>
                  <Label htmlFor={`live-${project.id}`} className="flex items-center mb-1">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Live URL (optionnel)
                  </Label>
                  <Input
                    id={`live-${project.id}`}
                    value={project.liveUrl}
                    onChange={(e) => handleProjectChange(project.id, 'liveUrl', e.target.value)}
                    onBlur={() => handleProjectBlur(project.id)}
                    disabled={editingProject !== project.id}
                    placeholder="https://..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Laissez vide si pas de site en ligne</p>
                </div>
              </div>
              <div>
                <Label htmlFor={`technologies-${project.id}`} className="flex items-center mb-1">Technologies (séparées par des virgules)</Label>
                <Textarea
                  id={`technologies-${project.id}`}
                  value={project.technologies.join(', ')}
                  onChange={(e) => handleTechnologiesChange(project.id, e.target.value)}
                  onBlur={() => handleProjectBlur(project.id)}
                  disabled={editingProject !== project.id}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor={`features-${project.id}`} className="flex items-center mb-1">Fonctionnalités (une par ligne)</Label>
                <Textarea
                  id={`features-${project.id}`}
                  value={project.features.join('\n')}
                  onChange={(e) => handleFeaturesChange(project.id, e.target.value)}
                  onBlur={() => handleProjectBlur(project.id)}
                  disabled={editingProject !== project.id}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdmProjects;